import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchLogsURL,
  getBankDocumentDownloadURL,
  getCustomerInvoiceStatusApprovelUrl,
  getCustomerInvoiceStatusUpdateUrl,
  getCustomerStatusBulkApprovelUrl,
  getDeleteOverdueDateURL,
  getFeeAdjustmentsURL,
  getLoanManagementquoteURL,
  getPasswordConfirmationURL,
  getUpdateDebtorNameURL,
  getUpdateOverdueDateURL,
  userManagementProfileURL
} from "../utils/constants/apiEndPoints";
import privateAxios from "../utils/axiosPrivate";
import { checklist, dashboard } from "../locales/en";
import { showToastType } from "../utils/constants/appConstants";
import { showToast } from "../utils/utils";

const initialState = {
  loadingBtn: false,
  loading: false,
  passwordLoading: false,
  statusInfo: false,
  bulkStatusInfo: false,
  approvedStatusInfo: false,
  statusErrorMessage: null,
  invDetailsData: null,
  invDetailsDataLoading: false,
  invLogDataLoading: false,
  invLogData: null,
  feeAdjustmentsData: null,
  feeAdjustmentsLoading: false
};

const invoiceDetailsSlice = createSlice({
  name: "invoiceDetails",
  initialState,
  reducers: {
    setStatusInfo(state, action) {
      state.statusInfo = action.payload;
    },
    setbulkStatusInfo(state, action) {
      state.bulkStatusInfo = action.payload;
    },
    setApprovedStatusInfo(state, action) {
      state.approvedStatusInfo = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAdvanceBankFileDownload.pending, (state) => {
        state.loadingBtn = true;
      })
      .addCase(getAdvanceBankFileDownload.fulfilled, (state) => {
        state.loadingBtn = false;
      })
      .addCase(getAdvanceBankFileDownload.rejected, (state, action) => {
        state.loadingBtn = false;
        if (action?.payload?.data instanceof Blob) {
          showToast(checklist?.errors?.noPayment, showToastType.error);
        }
      })

      .addCase(getUpdatOverdueDate.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUpdatOverdueDate.fulfilled, (state, action) => {
        state.loading = false;
        showToast(action.payload.data.message, showToastType.success);
      })
      .addCase(getUpdatOverdueDate.rejected, (state, action) => {
        state.loading = false;
        showToast(action.payload.data.message, showToastType.error);
      })

      .addCase(getDeleteOverdueDelete.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDeleteOverdueDelete.fulfilled, (state, action) => {
        state.loading = false;
        showToast(action.payload.data.message, showToastType.success);
      })
      .addCase(getDeleteOverdueDelete.rejected, (state, action) => {
        state.loading = false;
        showToast(action.payload.data.message, showToastType.error);
      })

      .addCase(getPasswordConfirmation.pending, (state) => {
        state.passwordLoading = true;
      })
      .addCase(getPasswordConfirmation.fulfilled, (state) => {
        state.passwordLoading = false;
      })
      .addCase(getPasswordConfirmation.rejected, (state, action) => {
        state.passwordLoading = false;
        showToast(action.payload.data.message, showToastType.error);
      })

      // Handle getCustomerInvoiceStatusData
      .addCase(getCustomerInvoiceStatusData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomerInvoiceStatusData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(getCustomerInvoiceStatusData.rejected, (state, action) => {
        state.loading = false;
        state.customerStatusData = action?.payload?.data?.errors || null;
        state.statusInfo = true;
      })

      // Handle getCustomerBulkStatusChangeData
      .addCase(getCustomerBulkStatusChangeData.pending, (state) => {
        state.bulkStatusInfo = false;
        state.approvedStatusInfo = false;
      })
      .addCase(getCustomerBulkStatusChangeData.fulfilled, (state) => {
        state.bulkStatusInfo = false;
        state.approvedStatusInfo = false;
      })
      .addCase(getCustomerBulkStatusChangeData.rejected, (state, action) => {
        const status = action.payload?.status ?? action.error?.status;
        const apiMessage =
          action.payload?.data?.message ||
          action.payload?.data?.errors?.[0]?.message ||
          action.error?.message ||
          null;
        if (
          status === 400 &&
          apiMessage === dashboard?.advanceAlert?.message_2
        ) {
          state.bulkStatusInfo = true;
        } else if (status === 400 && apiMessage === "Some validations failed") {
          state.customerStatusData = action?.payload?.data?.errors || null;
          state.approvedStatusInfo = true;
        }
      })

      .addCase(getInvoiceDetailsData.pending, (state) => {
        state.invDetailsDataLoading = true;
      })
      .addCase(getInvoiceDetailsData.fulfilled, (state, action) => {
        state.invDetailsDataLoading = false;
        state.invDetailsData = action.payload.data;
      })
      .addCase(getInvoiceDetailsData.rejected, (state) => {
        state.invDetailsDataLoading = false;
      })

      .addCase(getInvoiceLogData.pending, (state) => {
        state.invLogDataLoading = true;
      })
      .addCase(getInvoiceLogData.fulfilled, (state, action) => {
        state.invLogDataLoading = false;
        state.invLogData = action.payload.data;
      })
      .addCase(getInvoiceLogData.rejected, (state) => {
        state.invLogDataLoading = false;
      })

      .addCase(updateDebtorName.fulfilled, (state, action) => {
        const { debtorName } = action.payload;

        if (state.invDetailsData?.invoice) {
          state.invDetailsData.invoice.debatorName = debtorName;
        }
        showToast(
          action.payload.response?.data?.message,
          showToastType.success
        );
      })

      .addCase(updateDebtorName.rejected, (state, action) => {
        showToast(action.payload.response?.data?.message, showToastType.error);
      })

      .addCase(getFeeAdjusmentData.pending, (state) => {
        state.feeAdjustmentsLoading = true;
      })
      .addCase(getFeeAdjusmentData.fulfilled, (state, action) => {
        state.feeAdjustmentsLoading = false;
        state.feeAdjustmentsData = action.payload.data;
      })
      .addCase(getFeeAdjusmentData.rejected, (state, action) => {
        state.feeAdjustmentsLoading = false;
        showToast(action.payload.response?.data?.message, showToastType.error);
      });
  }
});

export const updateDebtorName = createAsyncThunk(
  "invoiceDetails/updateDebtorName",
  async ({ quoteId, debtorName }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(
        getUpdateDebtorNameURL,
        {},
        {
          params: {
            quoteId,
            newDebtorName: debtorName
          }
        }
      );
      return { response, debtorName };
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

// Export getAdvanceBankFileDownload thunk
export const getAdvanceBankFileDownload = createAsyncThunk(
  "invoiceDetails/getAdvanceBankFileDownload",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(
        getBankDocumentDownloadURL(payload.id),
        {
          responseType: "blob"
        }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

// Export getUpdatOverdueDate thunk
export const getUpdatOverdueDate = createAsyncThunk(
  "invoiceDetails/getUpdatOverdueDate",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.put(getUpdateOverdueDateURL, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

// Export getPasswordConfirmation thunk
export const getPasswordConfirmation = createAsyncThunk(
  "invoiceDetails/getPasswordConfirmation",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.post(
        getPasswordConfirmationURL,
        payload
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

// Export getDeleteOverdueDelete thunk
export const getDeleteOverdueDelete = createAsyncThunk(
  "invoiceDetails/getDeleteOverdueDelete",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.delete(getDeleteOverdueDateURL, {
        data: payload
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// async thunk Customer invoice status change
export const getCustomerInvoiceStatusData = createAsyncThunk(
  "invoiceDetails/getCustomerInvoiceStatusData",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(
        getCustomerInvoiceStatusApprovelUrl(id)
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

// async thunk Customer status approval data
export const getCustomerInvoiceStatusApprovalData = createAsyncThunk(
  "invoiceDetails/getCustomerInvoiceStatusApprovalData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.post(
        getCustomerInvoiceStatusUpdateUrl,
        payload
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

// async thunk Customer bulk status approval data
export const getCustomerBulkStatusChangeData = createAsyncThunk(
  "invoiceDetails/getCustomerBulkStatusChangeData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.post(
        getCustomerStatusBulkApprovelUrl,
        payload
      );
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  }
);

export const getProfilebyId = createAsyncThunk(
  "invoiceDetails/getProfilebyId",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(userManagementProfileURL, {
        params: payload
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getInvoiceDetailsData = createAsyncThunk(
  "invoiceDetails/getInvoiceDetailsData",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getLoanManagementquoteURL(id));
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getInvoiceLogData = createAsyncThunk(
  "invoiceDetails/getInvoiceLogData",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(fetchLogsURL, {
        params: payload
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getFeeAdjusmentData = createAsyncThunk(
  "invoiceDetails/getFeeAdjusmentData",
  async (quoteId, { rejectWithValue }) => {
    try {
      const response = await privateAxios.get(getFeeAdjustmentsURL(quoteId));

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const { setStatusInfo, setbulkStatusInfo, setApprovedStatusInfo } =
  invoiceDetailsSlice.actions;
export default invoiceDetailsSlice.reducer;
